import React, { useContext, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import * as Yup from 'yup';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { policyTypeProductNames } from '@ourbranch/lookups';

import { useStore } from 'core/store';
import { Loading } from 'core/components/loading';
import { AuthContext } from 'core/components/auth';
import { useToast } from 'core/components/toast';
import ScreenHeader from 'core/components/screen-header';
import { Button } from 'core/components/button';
import Field from 'core/components/form/form.v2';
import { types } from 'core/helpers/sanitize';
import { extractCleanOfferId } from 'offer/helpers/offer-id-helpers';
import { isMissingPrimaryFarmOperations } from 'common/helpers/farm-liability-precheck';
import {
  FarmUWQuestionsCard,
  PolicyRestrictionsCard,
  getFarmUWQuestionSections,
  getFarmUWInitialValues
} from 'common/components/farm/uw-questions-form';
import useStyles from './pre-bind-farm-UW.styles';

// Build the validation schema from the questions actually shown for this policy type. Home (Z) and
// Rental (Y) drop several farm-only questions (#31456 / #31507), so requiring them here would block
// submit on fields that never render.
const validationSchema = (selectedOption) => {
  const { eligibilityQuestions, propertyRiskFactorQuestions, operationalDetailsQuestions } =
    getFarmUWQuestionSections(selectedOption);
  const allQuestions = [...eligibilityQuestions, ...propertyRiskFactorQuestions, ...operationalDetailsQuestions];
  const schemaFields = {};

  allQuestions.forEach(({ id, showDetailsWhen }) => {
    const detailsRequiredWhenAnswer = showDetailsWhen ? showDetailsWhen[0] : 'true';

    schemaFields[id] = Yup.string().required();
    schemaFields[`${id}Details`] = Yup.string().when(id, {
      is: detailsRequiredWhenAnswer,
      then: (schema) => schema.required('Providing details is required'),
      otherwise: (schema) => schema
    });
  });

  schemaFields.restrictions = Yup.string().max(1000, 'Restrictions must be 1000 characters or fewer').nullable();

  schemaFields.signedFarmUWAttestation = Yup.boolean().oneOf([true], 'You must attest that your answers are accurate');

  return Yup.object().shape(schemaFields);
};

const PreBindFarmUW = observer(() => {
  const classes = useStyles();
  const history = useHistory();
  const { offer: offerStore } = useStore();
  const { offerId } = useParams();
  const cleanedOfferId = extractCleanOfferId(offerId);

  const { offer, getOffer } = offerStore;
  const session = useContext(AuthContext);
  const toast = useToast();
  const {
    user: { username }
  } = session;

  const farmUWInitialValues = getFarmUWInitialValues(offerStore?.offer?.quote?.preBindUWCheck?.farmVerification);

  // Home (Z) and Rental (Y) ask a reduced set of UW questions and a policy-type-aware validation
  // schema (#31456 / #31507). Memoize so we don't build a fresh Yup schema object on every render —
  // Formik treats a new schema reference as a change and would re-validate unnecessarily under
  // `validateOnChange` + MobX `observer`. Computed before the early returns below to keep hook order
  // stable; `selectedOption` only changes if the policy type changes mid-session (it doesn't here).
  const selectedOption = offerStore?.offer?.quote?.selectedOption;
  const questionSections = useMemo(() => getFarmUWQuestionSections(selectedOption), [selectedOption]);
  const farmValidationSchema = useMemo(() => validationSchema(selectedOption), [selectedOption]);

  const submitRecalculate = async (details) => {
    // Also guard a missing `quote` so a `{ ...undefined }` spread on
    // formattedDetails can't strip the payload of all offer state.
    const quote = offerStore.offer?.quote;
    if (!quote || isMissingPrimaryFarmOperations(quote)) {
      toast.notify({
        type: 'error',
        message: 'Primary farm operations is required on the Liability section before underwriting review.'
      });
      history.push(`/offer/${cleanedOfferId}`);
      return;
    }

    const existingApprovalDetails = offerStore.offer?.quote?.preBindUWCheck?.approvalDetails;
    const formattedDetails = {
      ...offerStore.offer.quote,
      preBindUWCheck: {
        farmVerification: details,
        attestationAgent: username,
        ...(existingApprovalDetails && {
          approvalDetails: {
            sentToUnderwritingAt: null,
            sentToUnderwritingBy: null,
            underwriterApprovedAt: null,
            underwriterApprovedBy: null
          }
        })
      },
      selectedOption: offerStore.selectedOption
    };
    const cleanedDetails = types.QuoteDetailsInput(formattedDetails);
    await offerStore.recalculateQuoteToCluster(offerStore.offer.id, cleanedDetails, history);
  };

  if (!offer) {
    getOffer(cleanedOfferId);
    return (
      <Grid container className={classes.loadingContainer}>
        <Loading noBackground />
      </Grid>
    );
  }

  if (!isFarmBasedPolicyType(offer.quote?.selectedOption)) {
    history.push(`/offer/${cleanedOfferId}`);
    return null;
  }

  if (!farmUWInitialValues || offerStore.loading) {
    return (
      <Grid container className={classes.loadingContainer}>
        <Loading noBackground />
      </Grid>
    );
  }

  // Home (Z) and Rental (Y) reuse the farm flow but must not say "farm" to staff/customers.
  // Full farm (F) keeps "farm". (#31217)
  const productNoun = policyTypeProductNames[selectedOption] || 'farm';

  const { eligibilityQuestions, propertyRiskFactorQuestions, operationalDetailsQuestions } = questionSections;

  return (
    <Formik
      validateOnChange
      initialValues={farmUWInitialValues}
      validationSchema={farmValidationSchema}
      onSubmit={submitRecalculate}
      validateOnBlur={false}
    >
      {({ handleSubmit }) => (
        <Grid container className={classes.container}>
          <ScreenHeader
            title="Underwriting review"
            subtitles={`This helps confirm that the ${productNoun} meets eligibility requirements. It only takes a few seconds.`}
            onBack={() => {
              history.push(`/offer/${cleanedOfferId}`);
            }}
            className={classes.header}
          />
          <FarmUWQuestionsCard questions={eligibilityQuestions} label="Eligibility history" />
          <FarmUWQuestionsCard questions={propertyRiskFactorQuestions} label="Property risk factors" />
          <FarmUWQuestionsCard questions={operationalDetailsQuestions} label="Operational details" />
          <PolicyRestrictionsCard />

          {/* footer */}
          <Grid className={classes.floatingContainer}>
            <Grid className={classes.footerContainer}>
              <Field
                id="signedFarmUWAttestation"
                name="signedFarmUWAttestation"
                type="checkbox"
                label="I attest that the answers to these questions are true and accurate to the best of my knowledge."
                mode="light"
              />
              <Button variant="contained" color="primary" onClick={handleSubmit} className={classes.saveButton}>
                Save
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Formik>
  );
});

export default PreBindFarmUW;
